import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { FileText, Download, Loader2, Eye, Sparkles, Save, Plus, X, Package, Clock, DollarSign, CheckCircle2, Users } from 'lucide-react';
import { listConfigItems } from '../utils/discoveryGridApi';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import type { ClientCompany, ConsultingFirm } from '../types';
import { initialSizingLines, pickSizingTemplate, sizingInputs, sizingTotals, sowSizingOf, type SizingLines, type SizingTemplate } from '../utils/sizingEstimate';
import { SizingLinesEditor } from './SizingLinesEditor';
import { SOWPreview } from './SOWPreview';
import { PAYMENT_DUE_OPTIONS, applyRequestTypeScope, previewProblems, requestTypeFromConfigItem, type RequestTypeLite } from '../utils/sowFromRequestTypes';
import { FixedBidQuoteLines, MarkupPercentField, type MarkupInput } from './FixedBidMarkup';
import { firmDefaultMarkupPercent, fixedBidQuote } from '../utils/fixedBidMarkup';

interface SOWGeneratorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client?: ClientCompany; // Made optional - will show client selector if not provided
  accessToken: string;
  consultingFirm?: ConsultingFirm;
  onNavigateToEngagementCatalog?: () => void;
}

interface EngagementType {
  id: string;
  name: string;
  category: string;
  defaultScope: string;
  defaultDeliverables: string[];
  standardDurationWeeks: number;
  estimatedHours: number;
  pricingModel: string;
  defaultHourlyRate?: number;
  defaultFixedPrice?: number;
  assumptions: string[];
  exclusions: string[];
}

interface SOWData {
  title: string;
  clientName: string;
  consultantName: string;
  description: string;
  scope: string;
  deliverables: string[];
  timeline: string;
  estimatedHours: number;
  hourlyRate: number;
  totalCost: number;
  /** The payment schedule (e.g. "50% upfront, 50% upon completion"); the PDF prints it under Payment Terms. */
  paymentTerms: string;
  /** When invoices are due: Due on receipt, Net 15 to 60 (the old full-page generator's Terms tab). */
  paymentDue: string;
  assumptions: string[];
  exclusions: string[];
  acceptanceCriteria: string;
  startDate: string;
  endDate: string;
  pricingModel: 'hourly' | 'fixed' | 'hybrid';
  fixedPrice?: number;
  /** A fixed bid's markup (contingency buffer), from the firm's default; `fixedPrice` is the base before it. */
  markupPercent: MarkupInput;
  /** The two sections carried over from the never-wired full-page generator (step 5 of the simplification plan). */
  teamMembers: string[];
  requestTypes: RequestTypeLite[];
}

export function SOWGenerator({ open, onOpenChange, client, accessToken, consultingFirm, onNavigateToEngagementCatalog }: SOWGeneratorProps) {
  const [engagementTypes, setEngagementTypes] = useState<EngagementType[]>([]);
  const [selectedEngagementType, setSelectedEngagementType] = useState<string>('');
  const [loadingTypes, setLoadingTypes] = useState(false);
  
  // SOW Templates state
  const [sowTemplates, setSOWTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [suggestedTemplates, setSuggestedTemplates] = useState<any[]>([]);
  
  // Company selection state (when no client is provided)
  const [companies, setCompanies] = useState<any[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [customCompanyName, setCustomCompanyName] = useState('');
  const [isNewProspect, setIsNewProspect] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientCompany | undefined>(client);

  // The estimate (Sell › Sizing's arithmetic, utils/sizingEstimate.ts): the templates the firm
  // may size with, the one picked for this client, and the quantities. The hours land in the
  // pricing fields; the breakdown travels with the SOW and prints in the PDF.
  // The engagement lead: a firm user picked on the SOW; the server defaults to the creator.
  const [firmUsers, setFirmUsers] = useState<Array<{ id: string; name?: string; email?: string }>>([]);
  const [engagementLeadId, setEngagementLeadId] = useState<string>('');
  const leadName = (id: string) => { const u = firmUsers.find((x) => x.id === id); return u ? (u.name || u.email || '') : ''; };

  const [sizingTemplates, setSizingTemplates] = useState<SizingTemplate[]>([]);
  const [sizingTemplateId, setSizingTemplateId] = useState<string>('');
  const [sizingLines, setSizingLines] = useState<SizingLines>({});
  const sizingTemplate = sizingTemplates.find((t) => t.id === sizingTemplateId) ?? null;
  const sizing = sizingTemplate ? sowSizingOf(sizingTemplate, sizingLines) : null;
  
  const [sowData, setSowData] = useState<SOWData>({
    title: `Statement of Work - ${client?.name || 'Client Name'}`,
    clientName: client?.name || 'Client Name',
    consultantName: consultingFirm?.name || '',
    description: '',
    scope: '',
    deliverables: [],
    timeline: '',
    estimatedHours: 0,
    hourlyRate: consultingFirm?.defaultHourlyRate || 150,
    totalCost: 0,
    paymentTerms: '50% upfront, 50% upon completion',
    paymentDue: 'Net 30',
    assumptions: [],
    exclusions: [],
    acceptanceCriteria: '',
    startDate: '',
    endDate: '',
    pricingModel: 'hourly',
    markupPercent: 0,
    teamMembers: [],
    requestTypes: [],
  });
  // The fixed bid as quoted: the base, the markup and the total the client pays.
  const quote = fixedBidQuote(sowData.fixedPrice, sowData.markupPercent);
  const [newTeamMember, setNewTeamMember] = useState('');
  // The client's request types in scope: its service-request config items (the request-types
  // route the old full-page generator read never existed).
  const [availableRequestTypes, setAvailableRequestTypes] = useState<RequestTypeLite[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!open || !client?.id) { setAvailableRequestTypes(null); return; }
    listConfigItems(client.id, accessToken)
      .then((items) => { if (!cancelled) setAvailableRequestTypes(items.filter((i: any) => i && i.category === 'service_requests').map(requestTypeFromConfigItem)); })
      .catch(() => { if (!cancelled) setAvailableRequestTypes([]); });
    return () => { cancelled = true; };
  }, [open, client?.id, accessToken]);

  const addTeamMember = () => {
    const name = newTeamMember.trim();
    if (!name) return;
    setSowData((d) => ({ ...d, teamMembers: [...d.teamMembers, name] }));
    setNewTeamMember('');
  };
  const toggleRequestType = (rt: RequestTypeLite) => {
    setSowData((d) => ({ ...d, requestTypes: d.requestTypes.some((x) => x.id === rt.id) ? d.requestTypes.filter((x) => x.id !== rt.id) : [...d.requestTypes, rt] }));
  };
  
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [branding, setBranding] = useState<any>(null);
  
  // Form state for adding items
  const [newDeliverable, setNewDeliverable] = useState('');
  const [newAssumption, setNewAssumption] = useState('');
  const [newExclusion, setNewExclusion] = useState('');

  useEffect(() => {
    if (open && consultingFirm) {
      loadEngagementTypes();
      loadSOWTemplates();
      loadBranding();
      loadSizing();
      
      // Load companies if no client was provided
      if (!client) {
        loadCompanies();
      }
    }
  }, [open, consultingFirm]);

  // Update suggested templates when engagement type changes
  useEffect(() => {
    if (selectedEngagementType) {
      const filtered = sowTemplates.filter(
        template => template.engagementTypeId === selectedEngagementType
      );
      setSuggestedTemplates(filtered);
    } else {
      setSuggestedTemplates([]);
    }
  }, [selectedEngagementType, sowTemplates]);

  const loadCompanies = async () => {
    if (!consultingFirm?.id) return;

    try {
      setLoadingCompanies(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/companies`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      } else {
        console.error('[SOW] Failed to load companies:', response.status);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleCompanySelection = (companyId: string) => {
    if (companyId === 'new') {
      setIsNewProspect(true);
      setSelectedCompanyId('');
      setSelectedClient(undefined);
      setSowData(prev => ({
        ...prev,
        clientName: '',
        title: 'Statement of Work - ',
      }));
    } else {
      setIsNewProspect(false);
      setSelectedCompanyId(companyId);
      const company = companies.find(c => c.id === companyId);
      if (company) {
        setSelectedClient({
          id: company.id,
          name: company.name,
          assignedTemplateIds: [],
        } as ClientCompany);
        setSowData(prev => ({
          ...prev,
          clientName: company.name,
          title: `Statement of Work - ${company.name}`,
        }));
      }
    }
  };

  const handleCustomCompanyNameChange = (name: string) => {
    setCustomCompanyName(name);
    setSowData(prev => ({
      ...prev,
      clientName: name,
      title: `Statement of Work - ${name}`,
    }));
  };

  useEffect(() => {
    // Auto-calculate total cost based on pricing model
    if (sowData.pricingModel === 'hourly') {
      setSowData(prev => ({
        ...prev,
        totalCost: prev.estimatedHours * prev.hourlyRate,
      }));
    } else if (sowData.pricingModel === 'fixed') {
      setSowData(prev => ({
        ...prev,
        totalCost: fixedBidQuote(prev.fixedPrice, prev.markupPercent).total,
      }));
    } else if (sowData.pricingModel === 'hybrid') {
      // For hybrid, use the lesser of T&M and fixed cap
      const tmCost = sowData.estimatedHours * sowData.hourlyRate;
      const cap = sowData.fixedPrice || tmCost;
      setSowData(prev => ({
        ...prev,
        totalCost: Math.min(tmCost, cap),
      }));
    }
  }, [sowData.estimatedHours, sowData.hourlyRate, sowData.pricingModel, sowData.fixedPrice, sowData.markupPercent]);

  const loadEngagementTypes = async () => {
    if (!consultingFirm?.id) return;

    try {
      setLoadingTypes(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${consultingFirm.id}/engagement-types`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[SOW] Loaded engagement types:', data);
        setEngagementTypes(data.engagementTypes || []);
      } else {
        console.error('[SOW] Failed to load engagement types:', response.status, await response.text());
        toast.error('Failed to load engagement types');
      }
    } catch (error) {
      console.error('Error loading engagement types:', error);
      toast.error('Error loading engagement types');
    } finally {
      setLoadingTypes(false);
    }
  };

  const loadSOWTemplates = async () => {
    if (!consultingFirm?.id) return;

    try {
      setLoadingTemplates(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${consultingFirm.id}/sow-templates`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[SOW] Loaded SOW templates:', data);
        setSOWTemplates(data.templates || []);
      } else {
        console.error('[SOW] Failed to load SOW templates:', response.status);
      }
    } catch (error) {
      console.error('Error loading SOW templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadBranding = async () => {
    if (!consultingFirm?.id) return;

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consulting-firms/${consultingFirm.id}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setBranding({
          logoUrl: data.firm.brandingLogoUrl,
          primaryColor: data.firm.brandingPrimaryColor || '#1E40AF',
          secondaryColor: data.firm.brandingSecondaryColor || '#3B82F6',
          accentColor: data.firm.brandingAccentColor || '#60A5FA',
          fontFamily: data.firm.brandingFontFamily || 'Inter',
          signatureUrl: data.firm.brandingSignatureUrl,
        });
      }
    } catch (error) {
      console.error('Error loading branding:', error);
    }
  };

  /** The sizable templates and the firm's real rate (`/consulting-firms/:firmId`, Firm › Settings › Scoping defaults); the client's platform picks the template. */
  const loadSizing = async () => {
    const headers = { 'Authorization': `Bearer ${accessToken}` };
    const base = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;
    try {
      const [tRes, fRes, uRes] = await Promise.all([
        fetch(`${base}/sizing/templates`, { headers }),
        consultingFirm?.id ? fetch(`${base}/consulting-firms/${encodeURIComponent(consultingFirm.id)}`, { headers }).catch(() => null) : Promise.resolve(null),
        consultingFirm?.id ? fetch(`${base}/firms/${encodeURIComponent(consultingFirm.id)}/users`, { headers }).catch(() => null) : Promise.resolve(null),
      ]);
      const tBody = tRes.ok ? await tRes.json().catch(() => ({})) : {};
      const fBody = fRes && fRes.ok ? await fRes.json().catch(() => ({})) : {};
      const uBody = uRes && uRes.ok ? await uRes.json().catch(() => ({})) : {};
      setFirmUsers(Array.isArray(uBody.users) ? uBody.users : []);
      const list: SizingTemplate[] = Array.isArray(tBody.templates) ? tBody.templates : [];
      setSizingTemplates(list);
      const rate = Number(fBody?.firm?.defaultHourlyRate);
      if (Number.isFinite(rate) && rate > 0) setSowData(prev => ({ ...prev, hourlyRate: rate }));
      setSowData(prev => ({ ...prev, markupPercent: firmDefaultMarkupPercent(fBody?.firm) }));
      const picked = pickSizingTemplate(list, client?.assignedTemplateIds?.[0]);
      if (picked) selectSizingTemplate(picked, list);
    } catch (error) {
      console.error('Error loading the sizing templates:', error);
    }
  };

  const selectSizingTemplate = (template: SizingTemplate | null, list: SizingTemplate[] = sizingTemplates) => {
    if (!template) { setSizingTemplateId(''); setSizingLines({}); return; }
    const lines = initialSizingLines(template);
    setSizingTemplateId(template.id);
    setSizingLines(lines);
    // A SOW with no hours yet takes the template's estimate at default quantities.
    const hours = Math.round(sizingTotals(sizingInputs(template, lines)).hoursEstimate);
    setSowData(prev => (prev.estimatedHours > 0 ? prev : { ...prev, estimatedHours: hours }));
    void list;
  };

  const applySizingHours = () => {
    if (!sizing) return;
    setSowData(prev => ({ ...prev, estimatedHours: sizing.hours }));
    toast.success(`Estimated hours set to ${sizing.hours} from ${sizing.templateName}`);
  };

  const handleEngagementTypeSelect = (typeId: string) => {
    setSelectedEngagementType(typeId);
    
    const engagementType = engagementTypes.find(t => t.id === typeId);
    if (!engagementType) return;

    // Calculate end date from start date + duration
    const startDate = new Date();
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + (engagementType.standardDurationWeeks * 7));

    // Auto-populate SOW from engagement type
    setSowData(prev => ({
      ...prev,
      scope: engagementType.defaultScope,
      deliverables: [...engagementType.defaultDeliverables],
      estimatedHours: engagementType.estimatedHours,
      timeline: `${engagementType.standardDurationWeeks} weeks`,
      pricingModel: engagementType.pricingModel as 'hourly' | 'fixed' | 'hybrid',
      hourlyRate: engagementType.defaultHourlyRate || prev.hourlyRate,
      fixedPrice: engagementType.defaultFixedPrice,
      assumptions: [...engagementType.assumptions],
      exclusions: [...engagementType.exclusions],
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    }));

    toast.success(`Pre-populated from "${engagementType.name}" template`);
  };

  const applyTemplate = (template: any) => {
    // Parse budget range to get estimated price
    const budgetMatch = template.budgetRange?.match(/\$?([\d,]+)K?\s*-\s*\$?([\d,]+)K?/);
    let estimatedPrice = 0;
    if (budgetMatch) {
      const min = parseInt(budgetMatch[1].replace(/,/g, '')) * (budgetMatch[1].includes('K') ? 1000 : 1);
      const max = parseInt(budgetMatch[2].replace(/,/g, '')) * (budgetMatch[2].includes('K') ? 1000 : 1);
      estimatedPrice = (min + max) / 2;
    }

    // Calculate dates based on estimated hours
    const startDate = new Date();
    const weeksEstimate = Math.ceil(template.estimatedHours / 40); // Assuming 40 hours per week
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + (weeksEstimate * 7));

    // Apply template data to SOW
    setSowData(prev => ({
      ...prev,
      description: template.description || prev.description,
      scope: template.scope || template.executiveSummary || prev.scope,
      deliverables: template.deliverables && template.deliverables.length > 0 ? [...template.deliverables] : prev.deliverables,
      estimatedHours: template.estimatedHours || prev.estimatedHours,
      timeline: `${weeksEstimate} weeks`,
      assumptions: template.assumptions && template.assumptions.length > 0 ? [...template.assumptions] : prev.assumptions,
      exclusions: template.exclusions && template.exclusions.length > 0 ? [...template.exclusions] : prev.exclusions,
      paymentTerms: template.paymentTerms || prev.paymentTerms,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      ...(estimatedPrice > 0 && { fixedPrice: estimatedPrice }),
    }));

    toast.success(`Applied template: "${template.name}"`);
  };

  const handleSave = async () => {
    if (!sowData.scope.trim()) {
      toast.error('Please fill in the scope of work');
      return;
    }

    try {
      setSaving(true);

      const engagementType = selectedEngagementType 
        ? engagementTypes.find(t => t.id === selectedEngagementType)
        : null;

      const payload = {
        companyId: client?.id,
        companyName: client?.name,
        engagementTypeId: engagementType?.id,
        engagementTypeName: engagementType?.name,
        clientName: sowData.clientName,
        projectTitle: sowData.title,
        startDate: sowData.startDate,
        endDate: sowData.endDate,
        scope: sowData.scope,
        deliverables: sowData.deliverables,
        timeline: sowData.timeline,
        // A fixed bid carries its base and markup; the server recomputes them (fixedBidMarkupCore.ts) and stores the bid total as the price.
        pricing: {
          model: sowData.pricingModel,
          hourlyRate: sowData.hourlyRate,
          fixedPrice: sowData.pricingModel === 'fixed' ? quote.total : sowData.fixedPrice,
          estimatedHours: sowData.estimatedHours,
          total: sowData.totalCost,
          ...(sowData.pricingModel === 'fixed' ? { basePrice: quote.base, markupPercent: quote.percent, markupAmount: quote.markup } : {}),
        },
        assumptions: sowData.assumptions,
        exclusions: sowData.exclusions,
        paymentTerms: sowData.paymentTerms,
        paymentDue: sowData.paymentDue,
        acceptanceCriteria: sowData.acceptanceCriteria,
        teamMembers: sowData.teamMembers,
        requestTypes: sowData.requestTypes,
        sizing: sizing ?? undefined,
        ...(engagementLeadId ? { engagementLeadId, engagementLeadName: leadName(engagementLeadId) } : {}),
        status: 'draft',
      };

      // POST /sows is the SOW creation route (the engagement record); it takes the company and the SOW body.
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/sows`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ companyId: client?.id, sow: { ...payload, name: sowData.title, title: sowData.title } }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save SOW');
      }

      const result = await response.json();
      toast.success('SOW saved');
      
      // Keep dialog open so user can generate PDF
      return result.sow;
    } catch (error) {
      console.error('Error saving SOW:', error);
      toast.error('Failed to save SOW');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!sowData.scope.trim()) {
      toast.error('Please fill in the scope of work');
      return;
    }

    setGenerating(true);
    // Open the print window now, inside the click, so popup blockers allow it; it is filled after the request.
    const printWindow = window.open('', '_blank');
    if (printWindow) printWindow.document.write('<p style="font-family:sans-serif;padding:2rem">Generating your Statement of Work…</p>');
    try {
      // First save the SOW
      await handleSave();

      // Then generate PDF
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/generate-sow-pdf`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            sowData: {
              ...sowData,
              ...(sowData.pricingModel === 'fixed' ? { basePrice: quote.base, markupPercent: quote.percent } : { markupPercent: 0 }),
              sizing: sizing ?? undefined,
              deliverables: sowData.deliverables.join('\n• '),
              assumptions: sowData.assumptions.join('\n• '),
              exclusions: sowData.exclusions.join('\n• '),
            },
            branding,
            firmId: consultingFirm?.id,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // The server returns a real PDF; hand it to the browser's PDF viewer (print and save live there).
      const pdf = await response.blob();
      const url = URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }));
      if (printWindow) {
        printWindow.location.href = url;
      } else {
        // The browser blocked the window: hand the document over as a download instead.
        const a = document.createElement('a');
        a.href = url;
        a.download = `SOW-${(sowData.clientName || 'client').replace(/\s+/g, '-')}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success('SOW PDF generated.');
      onOpenChange(false);
    } catch (error) {
      console.error('Error generating SOW:', error);
      if (printWindow) printWindow.close();
      toast.error(error instanceof Error && error.message ? error.message : 'Failed to generate SOW document');
    } finally {
      setGenerating(false);
    }
  };

  const handleAddDeliverable = () => {
    if (newDeliverable.trim()) {
      setSowData(prev => ({
        ...prev,
        deliverables: [...prev.deliverables, newDeliverable.trim()],
      }));
      setNewDeliverable('');
    }
  };

  const handleRemoveDeliverable = (index: number) => {
    setSowData(prev => ({
      ...prev,
      deliverables: prev.deliverables.filter((_, i) => i !== index),
    }));
  };

  const handleAddAssumption = () => {
    if (newAssumption.trim()) {
      setSowData(prev => ({
        ...prev,
        assumptions: [...prev.assumptions, newAssumption.trim()],
      }));
      setNewAssumption('');
    }
  };

  const handleRemoveAssumption = (index: number) => {
    setSowData(prev => ({
      ...prev,
      assumptions: prev.assumptions.filter((_, i) => i !== index),
    }));
  };

  const handleAddExclusion = () => {
    if (newExclusion.trim()) {
      setSowData(prev => ({
        ...prev,
        exclusions: [...prev.exclusions, newExclusion.trim()],
      }));
      setNewExclusion('');
    }
  };

  const handleRemoveExclusion = (index: number) => {
    setSowData(prev => ({
      ...prev,
      exclusions: prev.exclusions.filter((_, i) => i !== index),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Generate Statement of Work
          </DialogTitle>
          <DialogDescription>
            Select an engagement type template or create a custom SOW for {client?.name || 'Client Name'}
          </DialogDescription>
        </DialogHeader>

        {preview ? (
          <SOWPreview
            sow={sowData}
            sizingHours={sizing?.hours}
            firmName={consultingFirm?.name}
            leadName={engagementLeadId ? leadName(engagementLeadId) : undefined}
            onBack={() => setPreview(false)}
          />
        ) : (
        <div className="space-y-6 py-4">
          {/* Company Selection (shown only if no client was provided) */}
          {!client && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <Label className="text-base font-semibold">Select or Enter Company *</Label>
              <p className="text-sm text-muted-foreground">
                Choose an existing client or enter a new prospect name
              </p>
              <Select
                value={isNewProspect ? 'new' : selectedCompanyId}
                onValueChange={handleCompanySelection}
                disabled={loadingCompanies}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder={loadingCompanies ? "Loading companies..." : "Select a company or create new..."} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">
                    <div className="flex items-center gap-2">
                      <Plus className="size-4" />
                      <span className="font-medium">New Prospect (Enter company name)</span>
                    </div>
                  </SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {isNewProspect && (
                <div className="mt-3">
                  <Label htmlFor="customCompanyName">Prospect Company Name *</Label>
                  <Input
                    id="customCompanyName"
                    value={customCompanyName}
                    onChange={(e) => handleCustomCompanyNameChange(e.target.value)}
                    placeholder="Enter company name for this prospect"
                    className="bg-white"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    You can edit this name later if needed
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Engagement Type Selection */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="size-5 text-purple-600 mt-0.5" />
              <div className="flex-1">
                <Label htmlFor="engagement-type" className="text-base font-semibold mb-2 block">
                  Start with Engagement Template (Optional)
                </Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Select a pre-configured engagement type to auto-populate this SOW
                </p>
                <Select
                  value={selectedEngagementType}
                  onValueChange={handleEngagementTypeSelect}
                  disabled={loadingTypes}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder={
                      loadingTypes 
                        ? "Loading..." 
                        : engagementTypes.length === 0 
                          ? "No engagement types available - create them in the Engagement Catalog tab" 
                          : "Select an engagement type..."
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {engagementTypes.filter(type => type != null).length === 0 ? (
                      <div className="p-6 text-center space-y-3">
                        <p className="text-sm text-muted-foreground">
                          No engagement types available.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={(e) => {
                            e.preventDefault();
                            // Close this dialog and notify parent to switch to engagement catalog
                            onOpenChange(false);
                            toast.info('Navigate to the Engagement Catalog tab to create engagement types');
                            // TODO: Could emit an event or callback to switch tabs automatically
                            if (onNavigateToEngagementCatalog) {
                              onNavigateToEngagementCatalog();
                            }
                          }}
                        >
                          <Plus className="size-4 mr-2" />
                          Go to Engagement Catalog
                        </Button>
                      </div>
                    ) : (
                      engagementTypes.filter(type => type != null).map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          <div>
                            <div className="font-medium">{type.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {type.estimatedHours}h • {type.standardDurationWeeks} weeks
                            </div>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {selectedEngagementType && (
                  <Badge variant="secondary" className="mt-2">
                    Template Selected: {engagementTypes.find(t => t.id === selectedEngagementType)?.name}
                  </Badge>
                )}
              </div>
            </div>

            {/* Template Suggestions Panel - NEW */}
            {selectedEngagementType && suggestedTemplates.length > 0 && (
              <div className="mt-4 bg-gradient-to-r from-purple-50 to-yellow-50 border-2 border-purple-300 rounded-lg p-5 animate-in slide-in-from-top-2 duration-300">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Package className="size-5 text-purple-600" />
                    <h3 className="font-semibold text-purple-900">
                      Available SOW Templates
                    </h3>
                    <Badge className="bg-purple-600 text-white">
                      {suggestedTemplates.length}
                    </Badge>
                  </div>
                </div>
                
                <p className="text-sm text-gray-700 mb-4">
                  Quick-start with a pre-built SOW template for "{engagementTypes.find(t => t.id === selectedEngagementType)?.name}"
                </p>

                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {suggestedTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-purple-400 hover:shadow-md transition-all cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {template.name}
                          </h4>
                          <p className="text-sm text-gray-600 mb-3">
                            {template.description}
                          </p>
                        </div>
                      </div>

                      {/* Metadata */}
                      <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-3">
                        {template.estimatedHours && (
                          <div className="flex items-center gap-1">
                            <Clock className="size-3" />
                            <span>{template.estimatedHours} hours</span>
                          </div>
                        )}
                        {template.budgetRange && (
                          <div className="flex items-center gap-1">
                            <DollarSign className="size-3" />
                            <span>{template.budgetRange}</span>
                          </div>
                        )}
                        {template.usageCount !== undefined && (
                          <div className="flex items-center gap-1">
                            <CheckCircle2 className="size-3 text-green-600" />
                            <span>Used {template.usageCount} times</span>
                          </div>
                        )}
                      </div>

                      {/* Badges */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {template.category && (
                          <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                            {template.category}
                          </Badge>
                        )}
                        {template.industry && (
                          <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                            {template.industry}
                          </Badge>
                        )}
                        {template.tags && template.tags.slice(0, 2).map((tag: string, idx: number) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>

                      {/* Apply Button */}
                      <Button
                        onClick={() => applyTemplate(template)}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                        size="sm"
                      >
                        <Sparkles className="size-4 mr-2" />
                        Apply This Template
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No templates state */}
            {selectedEngagementType && suggestedTemplates.length === 0 && !loadingTemplates && (
              <div className="mt-4 bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4 text-center">
                <Package className="size-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-600 mb-2">
                  No SOW templates linked to this engagement type yet.
                </p>
                <p className="text-xs text-gray-500">
                  Create a template in the <span className="font-semibold">SOW Templates</span> tab and link it to "{engagementTypes.find(t => t.id === selectedEngagementType)?.name}"
                </p>
              </div>
            )}
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">Project Title *</Label>
              <Input
                id="title"
                value={sowData.title}
                onChange={(e) => setSowData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Statement of Work - Client Name"
              />
            </div>
            <div>
              <Label htmlFor="clientName">Client Name *</Label>
              <Input
                id="clientName"
                value={sowData.clientName}
                onChange={(e) => setSowData(prev => ({ ...prev, clientName: e.target.value }))}
                placeholder="Client company name"
              />
            </div>
          </div>

          {/* Timeline */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startDate">Start Date *</Label>
              <Input
                id="startDate"
                type="date"
                value={sowData.startDate}
                onChange={(e) => setSowData(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={sowData.endDate}
                onChange={(e) => setSowData(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Project Description</Label>
            <Textarea
              id="description"
              value={sowData.description}
              onChange={(e) => setSowData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief overview of the project"
              rows={2}
            />
          </div>

          {/* Scope of Work */}
          <div>
            <Label htmlFor="scope">Scope of Work *</Label>
            <Textarea
              id="scope"
              value={sowData.scope}
              onChange={(e) => setSowData(prev => ({ ...prev, scope: e.target.value }))}
              placeholder="Detailed description of work to be performed"
              rows={6}
            />
          </div>

          {/* Deliverables */}
          <div>
            <Label>Deliverables</Label>
            <div className="space-y-2">
              {sowData.deliverables.map((deliverable, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Badge variant="secondary" className="flex-1 text-left justify-start py-2">
                    {deliverable}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveDeliverable(index)}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  placeholder="Add a deliverable"
                  value={newDeliverable}
                  onChange={(e) => setNewDeliverable(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddDeliverable()}
                />
                <Button onClick={handleAddDeliverable} variant="outline">
                  <Plus className="size-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="border-t pt-4 space-y-4">
            <h3 className="font-semibold">Pricing</h3>
            
            <div>
              <Label htmlFor="pricing-model">Pricing Model</Label>
              <Select
                value={sowData.pricingModel}
                onValueChange={(value: 'hourly' | 'fixed' | 'hybrid') =>
                  setSowData(prev => ({ ...prev, pricingModel: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly (Time & Materials)</SelectItem>
                  <SelectItem value="fixed">Fixed Price</SelectItem>
                  <SelectItem value="hybrid">Hybrid (T&M with cap)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {sizingTemplates.length > 0 && (
              <div className="rounded-lg border bg-slate-50 p-3 space-y-3" data-testid="sow-sizing">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label htmlFor="sow-sizing-template">Estimate from a template</Label>
                    <p className="text-xs text-muted-foreground">Items × hours each, the same arithmetic as Sell › Sizing. Take lines out, change the items and the hours for this client, then use the hours or the price.</p>
                  </div>
                  <Select value={sizingTemplateId} onValueChange={(id) => selectSizingTemplate(sizingTemplates.find((t) => t.id === id) ?? null)}>
                    <SelectTrigger id="sow-sizing-template" className="w-56"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                    <SelectContent>
                      {sizingTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {sizingTemplate && (
                  <SizingLinesEditor compact template={sizingTemplate} lines={sizingLines} onChange={(id, patch) => setSizingLines((l) => ({ ...l, [id]: { ...(l[id] ?? { quantity: 0, hoursEach: 0, included: true }), ...patch } }))} />
                )}
                {sizing && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm">
                      {sizing.hoursMin !== sizing.hoursMax ? `${sizing.hoursMin}–${sizing.hoursMax} hours, estimate ${sizing.hours}` : `${sizing.hours} hours`}
                      {' '}· ${(sizing.hours * sowData.hourlyRate).toLocaleString()} at ${sowData.hourlyRate}/h
                    </span>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={applySizingHours} disabled={sowData.estimatedHours === sizing.hours}>
                        {sowData.estimatedHours === sizing.hours ? 'Hours in use' : `Use ${sizing.hours} hours`}
                      </Button>
                      {(sowData.pricingModel === 'fixed' || sowData.pricingModel === 'hybrid') && (
                        <Button type="button" size="sm" variant="outline" onClick={() => setSowData(prev => ({ ...prev, estimatedHours: sizing.hours, fixedPrice: sizing.hours * prev.hourlyRate }))}>
                          {`Use $${(sizing.hours * sowData.hourlyRate).toLocaleString()} as the ${sowData.pricingModel === 'hybrid' ? 'cap' : 'fixed price'}`}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {(sowData.pricingModel === 'hourly' || sowData.pricingModel === 'hybrid') && (
                <>
                  <div>
                    <Label htmlFor="estimatedHours">Estimated Hours</Label>
                    <Input
                      id="estimatedHours"
                      type="number"
                      value={sowData.estimatedHours}
                      onChange={(e) => setSowData(prev => ({ ...prev, estimatedHours: Number(e.target.value) }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="hourlyRate">Hourly Rate ($)</Label>
                    <Input
                      id="hourlyRate"
                      type="number"
                      value={sowData.hourlyRate}
                      onChange={(e) => setSowData(prev => ({ ...prev, hourlyRate: Number(e.target.value) }))}
                    />
                  </div>
                </>
              )}
              
              {(sowData.pricingModel === 'fixed' || sowData.pricingModel === 'hybrid') && (
                <div>
                  <Label htmlFor="fixedPrice">
                    {sowData.pricingModel === 'hybrid' ? 'Fixed Cap ($)' : 'Fixed Price ($)'}
                  </Label>
                  <Input
                    id="fixedPrice"
                    type="number"
                    value={sowData.fixedPrice || ''}
                    onChange={(e) => setSowData(prev => ({ ...prev, fixedPrice: Number(e.target.value) }))}
                  />
                </div>
              )}
              {sowData.pricingModel === 'fixed' && (
                <MarkupPercentField id="sow-markup" value={sowData.markupPercent} onChange={(v) => setSowData(prev => ({ ...prev, markupPercent: v }))} />
              )}
            </div>

            {sowData.pricingModel === 'fixed' && quote.percent > 0 && (
              <FixedBidQuoteLines quote={quote} testId="sow-fixed-bid-quote" className="text-sm rounded-md border p-3" />
            )}

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Total Cost:</span>
                <span className="text-2xl font-bold text-green-700">
                  ${sowData.totalCost.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="paymentDue">Payment terms</Label>
                <Select value={sowData.paymentDue} onValueChange={(v) => setSowData(prev => ({ ...prev, paymentDue: v }))}>
                  <SelectTrigger id="paymentDue" aria-label="Payment terms"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_DUE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="paymentTerms">Payment schedule</Label>
                <Input
                  id="paymentTerms"
                  value={sowData.paymentTerms}
                  onChange={(e) => setSowData(prev => ({ ...prev, paymentTerms: e.target.value }))}
                  placeholder="e.g., 50% upfront, 50% upon completion"
                />
              </div>
            </div>
          </div>

          {/* Team members (carried over from the full-page generator; step 5 of the simplification plan) */}
          <div className="border-t pt-4 space-y-3" data-testid="sow-team">
            <h3 className="font-semibold">Engagement lead</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sow-engagement-lead">
              <div>
                <Label htmlFor="sow-lead">Who leads this engagement</Label>
                <Select value={engagementLeadId} onValueChange={setEngagementLeadId}>
                  <SelectTrigger id="sow-lead"><SelectValue placeholder={firmUsers.length ? 'You, unless you pick someone' : 'You'} /></SelectTrigger>
                  <SelectContent>
                    {firmUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email || u.id}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Shown on Sell › SOWs and used by its "My SOWs" filter. You can change it later on the SOW.</p>
              </div>
            </div>
            <h3 className="font-semibold">Team members <span className="text-sm font-normal text-muted-foreground">(optional)</span></h3>
            {sowData.teamMembers.map((member, index) => (
              <div key={`${member}-${index}`} className="flex items-center gap-2">
                <Badge variant="secondary" className="flex-1 text-left justify-start py-2"><Users className="size-3 mr-2" aria-hidden="true" />{member}</Badge>
                <Button variant="ghost" size="sm" aria-label={`Remove ${member}`} onClick={() => setSowData((d) => ({ ...d, teamMembers: d.teamMembers.filter((_, i) => i !== index) }))}><X className="size-4" /></Button>
              </div>
            ))}
            <div className="flex gap-2">
              <Input placeholder="Add a team member" aria-label="Team member name" value={newTeamMember} onChange={(e) => setNewTeamMember(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTeamMember(); } }} />
              <Button variant="outline" onClick={addTeamMember} aria-label="Add team member"><Plus className="size-4" /></Button>
            </div>
          </div>

          {/* Request types in scope: the client's service-request config items */}
          <div className="border-t pt-4 space-y-3" data-testid="sow-request-types">
            <h3 className="font-semibold">Request types in scope {sowData.requestTypes.length > 0 && <Badge variant="secondary" className="ml-2">{sowData.requestTypes.length} selected</Badge>}</h3>
            {!client?.id ? (
              <p className="text-sm text-muted-foreground">Pick a client to choose its request types.</p>
            ) : availableRequestTypes === null ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="size-3.5 animate-spin" aria-hidden="true" /> Loading request types…</p>
            ) : availableRequestTypes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No request types configured for this client yet. They arrive as the client's service requests are set up in discovery.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableRequestTypes.map((rt) => {
                  const selected = sowData.requestTypes.some((x) => x.id === rt.id);
                  return (
                    <button type="button" key={rt.id} onClick={() => toggleRequestType(rt)} aria-pressed={selected} className={`rounded-full border px-3 py-1 text-sm ${selected ? 'border-blue-500 bg-blue-50 text-blue-900' : 'hover:border-gray-400'}`}>
                      {selected && <CheckCircle2 className="size-3.5 inline mr-1 text-blue-600" aria-hidden="true" />}{rt.name}
                      {typeof rt.fieldCount === 'number' && <span className="ml-1 text-muted-foreground">({rt.fieldCount} fields)</span>}
                    </button>
                  );
                })}
              </div>
            )}
            {sowData.requestTypes.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setSowData((d) => applyRequestTypeScope(d, d.requestTypes));
                  toast.success(`Scope and ${sowData.requestTypes.length * 5} deliverables written from ${sowData.requestTypes.length} request ${sowData.requestTypes.length === 1 ? 'type' : 'types'}`);
                }}
              >
                <Sparkles className="size-4 mr-2" aria-hidden="true" />
                Write scope and deliverables from the selected request types
              </Button>
            )}
          </div>

          {/* Assumptions */}
          <div className="border-t pt-4 space-y-4">
            <h3 className="font-semibold">Assumptions</h3>
            <div className="space-y-2">
              {sowData.assumptions.map((assumption, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Badge variant="outline" className="flex-1 text-left whitespace-normal py-2">
                    {assumption}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveAssumption(index)}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  placeholder="Add an assumption"
                  value={newAssumption}
                  onChange={(e) => setNewAssumption(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddAssumption()}
                />
                <Button onClick={handleAddAssumption} variant="outline">
                  <Plus className="size-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Exclusions */}
          <div className="border-t pt-4 space-y-4">
            <h3 className="font-semibold">Exclusions</h3>
            <div className="space-y-2">
              {sowData.exclusions.map((exclusion, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Badge variant="outline" className="flex-1 text-left whitespace-normal py-2">
                    {exclusion}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveExclusion(index)}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  placeholder="Add an exclusion"
                  value={newExclusion}
                  onChange={(e) => setNewExclusion(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddExclusion()}
                />
                <Button onClick={handleAddExclusion} variant="outline">
                  <Plus className="size-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Acceptance criteria (the field was in the data with no input; RESTORE_PLAN R3.5) */}
          <div className="border-t pt-4 space-y-2">
            <Label htmlFor="acceptanceCriteria">Acceptance criteria</Label>
            <Textarea
              id="acceptanceCriteria"
              rows={3}
              value={sowData.acceptanceCriteria}
              onChange={(e) => setSowData(prev => ({ ...prev, acceptanceCriteria: e.target.value }))}
              placeholder="e.g., Each deliverable is accepted when the client's engagement lead signs it off in PathKit"
            />
          </div>
        </div>
        )}

        {/* Actions */}
        <div className="flex justify-between border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPreview(!preview)}
              disabled={!preview && previewProblems(sowData).length > 0}
              title={!preview && previewProblems(sowData).length > 0 ? `Add ${previewProblems(sowData).join(', ')} to preview` : undefined}
            >
              <Eye className="size-4 mr-2" />
              {preview ? 'Back to edit' : 'Preview'}
            </Button>
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={saving || generating}
            >
              {saving && <Loader2 className="size-4 mr-2 animate-spin" />}
              <Save className="size-4 mr-2" />
              Save Draft
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={saving || generating || !sowData.scope.trim()}
              title={!sowData.scope.trim() ? 'Fill in the scope of work first' : undefined}
            >
              {generating && <Loader2 className="size-4 mr-2 animate-spin" />}
              <Download className="size-4 mr-2" />
              Generate PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}